import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    EntityValidations, ISpecificEntityFormProps, IApp03WorkflowStage, IUser, SaveStatus,
    IRiskAppIncidentAction,
    RiskAppIncidentAction,
    Contributor,
    CustomField
} from '../../types';
import { DateService, LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { CrDatePicker } from '../cr/CrDatePicker';
import { CrCheckbox } from '../cr/CrCheckbox';
import useMobileDetector from '../useMobileDetector';
import { CrCommandBar } from '../cr/CrCommandBar';
import { OrbUserContext } from '../OrbUserContext';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import EvidenceList from './EV/EvidenceList';


class IncidentActionFormValidations extends EntityValidations {
    public IncidentID: string = null;
    public AssignedTo: string = null;
}

interface IIncidentActionFormProps extends ISpecificEntityFormProps {
    workflowID: number;
    workflowTitle?: string;
    newTaskStageID: number;
}

export const IncidentActionFormPage = (props: IIncidentActionFormProps): React.ReactElement => {
    const { dataServices, lookupData, loadLookupData: { groups, directorates, orgLevel3s, customFieldDfns, users: { all: allUsers }, zCoreUserGroups, entityStatuses, riskAppIncidents, riskAppPriorities, riskAppSources, riskAppStages /*users: { enabled: enabledUsers }*/ } } = useContext(DataContext);
    const { userPermissions, orbConfig } = useContext(OrbUserContext);
    const { userContext } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;
    const isMobile = useMobileDetector();
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => allUsers(), [allUsers]);
    useMemo(() => riskAppIncidents(), [riskAppIncidents]);
    useMemo(() => zCoreUserGroups(), [zCoreUserGroups]);
    useMemo(() => riskAppPriorities(), [riskAppPriorities]);
    useMemo(() => riskAppSources(), [riskAppSources]);
    useMemo(() => riskAppStages(), [riskAppStages]);
    useMemo(() => groups(), [groups]);
    useMemo(() => directorates(), [directorates]);
    useMemo(() => orgLevel3s(), [orgLevel3s]);
    useMemo(() => customFieldDfns(), [customFieldDfns]);


    const [showArchived, setShowArchived] = useState<boolean>(true);
    const [evidence_ListFilterText, setEvidence_ListFilterText] = useState<string>('');
    const [taskAddedOn, setTaskAddedOn] = useState<string>('');


    const validateEntity = (incidentAction: IRiskAppIncidentAction): Promise<IncidentActionFormValidations> => {
        const errors = new IncidentActionFormValidations();

        if (incidentAction.Title === null || incidentAction.Title === '') {
            errors.Title = `Action name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }
        if (incidentAction.IncidentID === null || isNaN(incidentAction.IncidentID)) {
            errors.IncidentID = `Incident is required`;
            errors.Valid = false;
        }
        else
            errors.IncidentID = null;

        return Promise.resolve(errors);
    };

    const handleEvidence_ChangeFilterText = (value: string): void => {
        setEvidence_ListFilterText(value);
    }

    return (
        <div className={`${styles.cr} ${styles.updatesContainer}`} style={{ width: '100%' }}>
            <EntityForm<IRiskAppIncidentAction, IncidentActionFormValidations>
                {...props}
                entityName='Incident'
                mode='page'
                renderFormFields={(changeHandlers, formState, saveEntity) => {
                    const { FormData: formData, ValidationErrors: errors, FormIsDirty, FormSaveStatus } = formState;

                    let commandBarItems: ICommandBarItemProps[] = [];

                    if (FormSaveStatus === SaveStatus.Pending) {
                        commandBarItems.push({
                            key: 'Save',
                            text: 'Saving...',
                            iconProps: { iconName: 'Sync' },
                            disabled: true,
                        });
                    } else if (!FormIsDirty) {
                        commandBarItems.push({
                            key: 'Close',
                            text: 'Close',
                            iconProps: { iconName: 'Cancel' },
                            onClick: (ev, item) => {
                                props.onCancelled();
                            },
                        });
                    } else {
                        commandBarItems.push(
                            {
                                key: 'Save',
                                text: 'Save',
                                iconProps: { iconName: 'Save' },
                                onClick: (ev, item) => {
                                    saveEntity();
                                },
                            },
                            {
                                key: 'Cancel',
                                text: 'Cancel',
                                iconProps: { iconName: 'Cancel' },
                                onClick: (ev, item) => {
                                    props.onCancelled();
                                },
                            }
                        );
                    }

                    //Labels row 3
                    const stageLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Stage</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const priotityLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Priority</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 4
                    const actualStartDateLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Actual Start Date</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const targetCompletionDateLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Target Completion Date </span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 5
                    const reportedByLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Reported By</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const statusLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Status</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;



                    return (
                        <div>
                            {/* <CrLoadingOverlay isLoading={isLoading} opaque={true} />*/}
                            <CrCommandBar items={commandBarItems} />
                            {/* <div className='tasks-info'>
                                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                    <div className={styles.sectionATitle}>
                                        {props.entityId > 0 ? formData?.Title : 'New Incident'}
                                    </div>
                                </div>
                            </div> */}

                            {/* <div className="info-table-container">
                                <table className="info-table" cellSpacing="0" cellPadding="10">
                                    <tbody>
                                        <tr>
                                            <td className="label">
                                                Workstream
                                            </td>
                                            <td className="value">
                                                {props.workflowTitle}
                                            </td>
                                            <td className="label">
                                                Added By
                                            </td>
                                            <td className="value">
                                                {props.entityId > 0
                                                    ? taskInfo?.['AddedBy']?.['Title']
                                                    : ''
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="label">
                                                Stage
                                            </td>
                                            <td className="value">
                                                {props.entityId > 0
                                                    ? taskInfo?.WorkflowStage?.Title
                                                    : workflowStages?.find(s => s.ID === props.newTaskStageID)?.Title || ''}

                                            </td>
                                            <td className="label">
                                                Added On
                                            </td>
                                            <td className="value">
                                                {taskAddedOn}
                                            </td>
                                        </tr>

                                    </tbody>
                                </table>
                            </div> */}

                            {/* Basic Details Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '10px' /*marginTop: '30px'*/ }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Basic Details</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Action Name</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    maxLength={300}
                                                    value={formData.Title}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                                                    errorMessage={errors.Title}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2nd row */}
                                    {!(props.showEntityDescription1 === true) && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Incident</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppIncidents)}
                                                    selectedKey={formData.IncidentID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'IncidentID')}
                                                    required={true}
                                                    errorMessage={errors.IncidentID}
                                                />
                                            </div>
                                        </div>
                                    </div>}

                                    {/* 3rd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Full Description</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    multiline
                                                    rows={6}
                                                    maxLength={10000}
                                                    value={formData.Description}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Description')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 4th row */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {actualStartDateLabel}
                                            {targetCompletionDateLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && actualStartDateLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.ActualStartDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'ActualStartDate')}
                                                />
                                            </div>
                                            {isMobile && targetCompletionDateLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.TargetCompletionDate)}
                                                    minDate={DateService.removeTimezoneOffset(formData.ActualStartDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'TargetCompletionDate')}
                                                //errorMessage={errors.EndDate}
                                                />
                                            </div>
                                        </div>
                                    </div>


                                    {/* 5th row */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {stageLabel}
                                            {statusLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && stageLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppStages.filter(x => x.ForIncident === true))}
                                                    selectedKey={formData.StageID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'StageID')}
                                                />
                                            </div>
                                            {isMobile && statusLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                                                    selectedKey={formData.EntityStatusID}
                                                    onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Ownership and Roles Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Ownership and Roles</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>

                                    {formData.ID > 0 && <>
                                        {/* 1st row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Owner Type</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDropdown

                                                        options={LookupService.entitiesToSelectableOptions([
                                                            { ID: 0, Title: orbConfig.OrgTypeLabel },
                                                            { ID: 1, Title: orbConfig.OrgL1Label },
                                                            { ID: 2, Title: orbConfig.OrgL2Label },
                                                            { ID: 3, Title: orbConfig.OrgL3Label },
                                                        ])}
                                                        selectedKey={formData.Incident?.OwnerTypeID}
                                                        onClick={(e) => e.preventDefault()}
                                                    //disabled
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2nd row */}
                                        {formData.Incident?.OwnerTypeID > 0 && <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}>
                                                            {formData.Incident?.OwnerTypeID === 1 && <span>{`Owner ${orbConfig.OrgL1Label}`}</span>}
                                                            {formData.Incident?.OwnerTypeID === 2 && <span>{`Owner ${orbConfig.OrgL2Label}`}</span>}
                                                            {formData.Incident?.OwnerTypeID === 3 && <span>{`Owner ${orbConfig.OrgL3Label}`}</span>}
                                                        </div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    {formData.Incident?.OwnerTypeID === 1 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.Groups)}
                                                            selectedKey={formData.Incident?.OwnerL1ID}
                                                            onClick={(e) => e.preventDefault()}
                                                        />
                                                    }
                                                    {formData.Incident?.OwnerTypeID === 2 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                                            selectedKey={formData.Incident?.OwnerL2ID}
                                                            onClick={(e) => e.preventDefault()}
                                                        />
                                                    }
                                                    {formData.Incident?.OwnerTypeID === 3 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                                            selectedKey={formData.Incident?.OwnerL3ID}
                                                            onClick={(e) => e.preventDefault()}
                                                        //disabled
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        </div>}

                                        {/* 3rd row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Managed By Type</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions([
                                                            { ID: 2, Title: orbConfig.OrgL2Label },
                                                            { ID: 3, Title: orbConfig.OrgL3Label },
                                                        ])}
                                                        selectedKey={formData.Incident?.ManagedByTypeID}
                                                        onClick={(e) => e.preventDefault()}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 4th row */}
                                        {formData.Incident?.ManagedByTypeID > 1 && <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}>
                                                            {formData.Incident?.ManagedByTypeID === 2 && <span>{`Managed by ${orbConfig.OrgL2Label}`}</span>}
                                                            {formData.Incident?.ManagedByTypeID === 3 && <span>{`Managed by ${orbConfig.OrgL3Label}`}</span>}
                                                        </div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    {formData.Incident?.ManagedByTypeID === 2 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                                            selectedKey={formData.Incident?.ManagedByL2ID}
                                                            onClick={(e) => e.preventDefault()}
                                                        />
                                                    }
                                                    {formData.Incident?.ManagedByTypeID === 3 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                                            selectedKey={formData.Incident?.ManagedByL3ID}
                                                            onClick={(e) => e.preventDefault()}
                                                        />
                                                    }

                                                </div>
                                            </div>
                                        </div>}
                                    </>}

                                    {/* 5th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Action Owner</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrUserPicker
                                                    users={lookupData.Users?.All}
                                                    selectedUsers={formData.ActionOwnerID && [formData.ActionOwnerID]}
                                                    onChange={v => changeHandlers.changeUserPicker(v, 'ActionOwnerID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 6th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Contributors</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrUserPicker
                                                    users={lookupData.Users?.All}
                                                    itemLimit={3}
                                                    selectedUsers={formData.Contributors?.map(c => c.ContributorUserID)}
                                                    onChange={v => changeHandlers.changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                                                />
                                            </div>
                                        </div>
                                    </div>


                                </div>
                            </div>




                        </div>
                    );
                }}
                loadEntity={id => dataServices.riskAppIncidentActionService.read(id, true, true)}
                onAfterLoad={(incident) => {
                    //setShowArchived(task.Archived === true ? false : true);
                }}
                loadNewEntity={() => new RiskAppIncidentAction()}
                loadEntityValidations={() => new IncidentActionFormValidations()}
                onValidateEntity={validateEntity}
                onCreate={incident => dataServices.riskAppIncidentActionService.create(incident)}
                onUpdate={incident => dataServices.riskAppIncidentActionService.update(incident.ID, incident)}
                parentEntities={dataServices.riskAppIncidentActionService.parentEntities}
                childEntities={[
                    {
                        ObjectParentProperty: 'Contributors',
                        ParentIdProperty: 'RiskAppIncidentActionID',
                        ChildIdProperty: 'ContributorUserID',
                        ChildService: dataServices.contributorService
                    },
                    // {
                    //     ObjectParentProperty: 'CustomFields',
                    //     ParentIdProperty: 'RiskAppIncidentID',
                    //     ChildIdProperty: 'CustomFieldDefnID',
                    //     ChildService: dataServices.customFieldService
                    // }
                ]}


            />
            {props.entityId > 0 &&
                <>
                    <div>
                        <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                            <div style={{ display: 'flex' }}>
                                <div className={styles.sectionATitle}>Discussion, General Comments and File Attachments</div>
                            </div>
                        </div>
                    </div>
                    <div>

                        <div style={{ minHeight: '120px' }}>
                            <EvidenceList
                                evidenceFor='IncidentAction'
                                incidentActionID={props.entityId}
                                issueID={null}
                                issueActionID={null}
                                incidentID={null}
                                riskID={null}
                                riskMitigationActionID={null}
                                currentUserId={currentUserId}
                                superUserPermission={userPermissions.UserIsSystemAdmin()}
                                isViewOnly={false}
                                filterText={evidence_ListFilterText}
                                onChangeFilterText={handleEvidence_ChangeFilterText}
                                evChangesCounter={1}
                                {...props}
                                errorHandling={props.errorHandling}
                            />
                        </div>

                    </div>
                </>
            }

        </div>
    );
};
